import { z } from "zod";

const utf8ByteLength = (value: string): number => new TextEncoder().encode(value).byteLength;

const nullableFormText = (maximum: number) =>
  z
    .union([z.string(), z.null()])
    .optional()
    .transform((value) => {
      const trimmed = value?.trim() ?? "";
      return trimmed.length === 0 ? null : trimmed;
    })
    .refine((value) => value === null || utf8ByteLength(value) <= maximum);

export const linenSubscriptionIdSchema = z.string().regex(/^[0-9a-f]{32}$/u);
export const linenPackageIdSchema = z.string().regex(/^[0-9a-f]{32}$/u);
export const linenBillingCycleSchema = z.enum(["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY", "CUSTOM"]);
export const linenSubscriptionStatusSchema = z.enum(["DRAFT", "ACTIVE", "CANCELLED"]);

const versionNoSchema = z.number().int().min(1);
const isoDateTimeSchema = z.string().datetime({ offset: true });
const usdMinorSchema = z.number().int().min(0).max(99_999_999);

export const publishedLinenPackageSchema = z.strictObject({
  id: linenPackageIdSchema,
  packageCode: z.string().min(1).max(80),
  name: z.string().min(1).max(200),
  description: z.string().nullable(),
  priceMinor: usdMinorSchema,
  currencyCode: z.literal("USD"),
  billingCycle: linenBillingCycleSchema,
  status: z.enum(["PUBLISHED", "COMING_SOON"]),
  publishedAt: isoDateTimeSchema.nullable(),
});

export const linenPackageCatalogResponseSchema = z.strictObject({
  data: z.array(publishedLinenPackageSchema),
});

const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, "Enter a calendar date")
  .refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === (month ?? 0) - 1 &&
      date.getUTCDate() === day
    );
  }, "Enter a valid calendar date");
const deliveryTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/u, "Enter a time in 24-hour format");
const deliveryTimezoneSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(
    /^(?:UTC|[A-Za-z]+(?:[_+-][A-Za-z0-9]+)*(?:\/[A-Za-z0-9_+-]+)+)$/u,
    "Select a supported IANA time zone",
  );

function minutesSinceMidnight(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

const deliveryPreferenceFields = {
  firstDeliveryDate: calendarDateSchema,
  deliveryWindowStart: deliveryTimeSchema,
  deliveryWindowEnd: deliveryTimeSchema,
  deliveryTimezone: deliveryTimezoneSchema,
  deliveryRule: nullableFormText(500),
};

function addDeliveryWindowIssues(
  value: Pick<
    z.infer<z.ZodObject<typeof deliveryPreferenceFields>>,
    "deliveryWindowStart" | "deliveryWindowEnd"
  >,
  context: z.RefinementCtx,
): void {
  const duration =
    minutesSinceMidnight(value.deliveryWindowEnd) - minutesSinceMidnight(value.deliveryWindowStart);
  if (duration < 60 || duration > 240) {
    context.addIssue({
      code: "custom",
      path: ["deliveryWindowEnd"],
      message: "Choose a delivery window between 60 and 240 minutes",
    });
  }
}

export const createLinenSubscriptionInputSchema = z.strictObject({
  propertyId: linenSubscriptionIdSchema,
});

export const createLinenConfigurationInputSchema = z
  .strictObject({
    packageId: linenPackageIdSchema,
    ...deliveryPreferenceFields,
    pickupRequested: z.boolean(),
    pickupInstructions: nullableFormText(2_000),
  })
  .superRefine((value, context) => {
    addDeliveryWindowIssues(value, context);
    if (!value.pickupRequested && value.pickupInstructions !== null) {
      context.addIssue({
        code: "custom",
        path: ["pickupInstructions"],
        message: "Remove pickup instructions when pickup is not requested",
      });
    }
  });

export const activateLinenSubscriptionInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  configurationVersionNo: versionNoSchema,
  confirmation: z.literal(true),
});

export const cancelLinenSubscriptionInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  confirmation: z.literal(true),
});

export const linenConfigurationSchema = z.strictObject({
  id: linenSubscriptionIdSchema,
  subscriptionId: linenSubscriptionIdSchema,
  versionNo: versionNoSchema,
  packageId: linenPackageIdSchema,
  package: publishedLinenPackageSchema,
  cadence: linenBillingCycleSchema,
  firstDeliveryDate: calendarDateSchema.nullable(),
  deliveryWindowStart: deliveryTimeSchema.nullable(),
  deliveryWindowEnd: deliveryTimeSchema.nullable(),
  deliveryTimezone: deliveryTimezoneSchema.nullable(),
  deliveryRule: z.string().nullable(),
  pickupRequested: z.boolean(),
  pickupInstructions: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});

const linenSubscriptionCoreSchema = z.strictObject({
  id: linenSubscriptionIdSchema,
  propertyId: linenSubscriptionIdSchema,
  status: linenSubscriptionStatusSchema,
  versionNo: versionNoSchema,
  currentConfiguration: linenConfigurationSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const linenSubscriptionSummarySchema = linenSubscriptionCoreSchema;
export const linenSubscriptionDetailSchema = linenSubscriptionCoreSchema.extend({
  configurations: z.array(linenConfigurationSchema),
});

export const linenSubscriptionListResponseSchema = z.strictObject({
  data: z.array(linenSubscriptionSummarySchema),
});
export const linenSubscriptionResponseSchema = z.strictObject({
  status: z.literal("ok"),
  subscription: linenSubscriptionDetailSchema,
});
export const createLinenSubscriptionResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  subscription: linenSubscriptionDetailSchema,
});
export const createLinenConfigurationResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  subscription: linenSubscriptionDetailSchema,
});
export const activateLinenSubscriptionResponseSchema = z.strictObject({
  status: z.enum(["activated", "existing"]),
  subscription: linenSubscriptionDetailSchema,
});
export const cancelLinenSubscriptionResponseSchema = z.strictObject({
  status: z.enum(["cancelled", "existing"]),
  subscription: linenSubscriptionDetailSchema,
});

export type PublishedLinenPackage = z.infer<typeof publishedLinenPackageSchema>;
export type LinenBillingCycle = z.infer<typeof linenBillingCycleSchema>;
export type LinenSubscriptionStatus = z.infer<typeof linenSubscriptionStatusSchema>;
export type CreateLinenSubscriptionInput = z.infer<typeof createLinenSubscriptionInputSchema>;
export type CreateLinenConfigurationInput = z.infer<typeof createLinenConfigurationInputSchema>;
export type ActivateLinenSubscriptionInput = z.infer<typeof activateLinenSubscriptionInputSchema>;
export type CancelLinenSubscriptionInput = z.infer<typeof cancelLinenSubscriptionInputSchema>;
export type LinenConfiguration = z.infer<typeof linenConfigurationSchema>;
export type LinenSubscriptionSummary = z.infer<typeof linenSubscriptionSummarySchema>;
export type LinenSubscriptionDetail = z.infer<typeof linenSubscriptionDetailSchema>;
